import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AjaxService } from '../../../service/ajax.service';
import { UtilService } from '../../../service/util.service';
import { API } from '../../../../environments/environment';

interface Assessor {
  id: string;
  text: string;
}

interface HorarioDisponivelApi {
  idfisioterapeuta?: string | number;
  horaformatada?: string | number;
  horafimformatada?: string | number;
  datasemhoraformatada?: string | number;
  dataformatadaanomesdiaagenda?: string | number;
  diasemananome?: string | number;
  ishorariobloqueado?: boolean;
  presencial_online?: string | number;
  presencialonline?: string | number;
  presencialognline?: string | number;
}

interface DiaAgenda {
  data: string;
  dataIso: string;
  diaSemanaNome: string;
  horarios: {
    inicio: string;
    fim: string;
    idAssessor: string;
    presencialOnline: string;
    bloqueado: boolean;
  }[];
}

@Component({
  selector: 'app-agendar-horarios',
  templateUrl: './agendar-horarios.component.html',
  styleUrls: ['./agendar-horarios.component.scss']
})
export class AgendarHorariosComponent implements OnInit {
  // Formulário/filtros
  assessorSelecionado: string = '';
  tipoSelecionado: string = 'online';

  // Dados
  assessores: Assessor[] = [];
  diasAgenda: DiaAgenda[] = [];
  loading: boolean = false;
  loadingHorarios: boolean = false;

  // Navegação de mês
  mesSelecionado: Date = new Date();
  private readonly meses = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  constructor(
    private router: Router,
    private ajaxService: AjaxService,
    private util: UtilService
  ) { }

  ngOnInit(): void {
    this.carregarAssessores();
  }

  carregarAssessores(): void {
    this.loading = true;
    this.util.showLoading();

    const url = API + 'empresa/AgendaClinica/GetDropDownMedicos';

    this.ajaxService.get(url).subscribe({
      next: (response) => {
        this.util.closeLoading();
        this.loading = false;

        if (response && response.data && Array.isArray(response.data)) {
          this.assessores = response.data
            .map((item: any) => this.util.converterParaLowercase(item))
            .filter((item: any) => item?.id && item?.text);

          if (this.assessores.length > 0) {
            this.assessorSelecionado = this.assessores[0].id;
            this.carregarHorariosDisponiveis();
          }
        } else {
          this.assessores = [];
        }
      },
      error: (error) => {
        this.util.closeLoading();
        this.loading = false;
        this.util.toasterShowError('Erro', 'Erro ao carregar lista de assessores');
        this.assessores = [];
      }
    });
  }

  get mesAnoLabel(): string {
    return `${this.meses[this.mesSelecionado.getMonth()]} ${this.mesSelecionado.getFullYear()}`.toUpperCase();
  }

  navegarAnterior(): void {
    this.mesSelecionado = new Date(this.mesSelecionado.getFullYear(), this.mesSelecionado.getMonth() - 1, 1);
    this.carregarHorariosDisponiveis();
  }

  navegarProximo(): void {
    this.mesSelecionado = new Date(this.mesSelecionado.getFullYear(), this.mesSelecionado.getMonth() + 1, 1);
    this.carregarHorariosDisponiveis();
  }

  irParaHoje(): void {
    const hoje = new Date();
    this.mesSelecionado = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
    this.carregarHorariosDisponiveis();
  }

  onAssessorChange(): void {
    this.carregarHorariosDisponiveis();
  }

  carregarHorariosDisponiveis(): void {
    if (!this.assessorSelecionado) {
      this.diasAgenda = [];
      return;
    }

    const payload = {
      mes: this.mesSelecionado.getMonth() + 1,
      ano: this.mesSelecionado.getFullYear(),
      idMedico: this.assessorSelecionado
    };

    const url = API + 'empresa/AgendaClinica/GetAgendaDisponivelMedico';
    this.loadingHorarios = true;
    this.util.showLoading();

    this.ajaxService.post(url, payload).subscribe({
      next: (response) => {
        this.util.closeLoading();
        this.loadingHorarios = false;

        const lista = response?.data && Array.isArray(response.data) ? response.data : [];
        const normalizados: HorarioDisponivelApi[] = lista
          .filter((item: any) => item && typeof item === 'object')
          .map((item: any) => this.util.converterParaLowercase(item));

        this.diasAgenda = this.agruparAgenda(normalizados);
      },
      error: () => {
        this.util.closeLoading();
        this.loadingHorarios = false;
        this.diasAgenda = [];
        this.util.toasterShowError('Erro', 'Erro ao carregar horários disponíveis');
      }
    });
  }

  private agruparAgenda(lista: HorarioDisponivelApi[]): DiaAgenda[] {
    const mapa = new Map<string, DiaAgenda>();

    lista.forEach((item) => {
      const dataIso = this.toText(item.dataformatadaanomesdiaagenda);
      const dataBr = this.toText(item.datasemhoraformatada);
      const inicio = this.toText(item.horaformatada);
      const fim = this.toText(item.horafimformatada);
      const idAssessor = this.toText(item.idfisioterapeuta || this.assessorSelecionado);
      const presencial = this.toText(item.presencial_online || item.presencialonline || item.presencialognline || 'O').toUpperCase();
      const bloqueado = !!item.ishorariobloqueado;
      const diaSemanaNome = this.toText(item.diasemananome).toUpperCase();

      if (!dataIso || !inicio || !fim) return;
      if (bloqueado) return;
      if (this.tipoSelecionado === 'online' && presencial !== 'O') return;
      if (this.tipoSelecionado === 'presencial' && presencial !== 'P') return;

      if (!mapa.has(dataIso)) {
        mapa.set(dataIso, {
          data: dataBr || dataIso,
          dataIso,
          diaSemanaNome: diaSemanaNome || 'DIA',
          horarios: []
        });
      }

      mapa.get(dataIso)!.horarios.push({
        inicio,
        fim,
        idAssessor,
        presencialOnline: presencial,
        bloqueado
      });
    });

    return Array.from(mapa.values())
      .sort((a, b) => a.dataIso.localeCompare(b.dataIso))
      .map((dia) => ({
        ...dia,
        horarios: dia.horarios.sort((a, b) => a.inicio.localeCompare(b.inicio))
      }));
  }

  private toText(value: unknown): string {
    if (value === null || value === undefined) return '';
    return String(value).trim();
  }

  selecionarTipo(tipo: string): void {
    this.tipoSelecionado = tipo;
    this.carregarHorariosDisponiveis();
  }

  isHorarioPresencial(presencialOnline: string): boolean {
    return this.toText(presencialOnline).toUpperCase() === 'P';
  }

  selecionarHorario(dia: DiaAgenda, horario: { inicio: string; fim: string; idAssessor: string; presencialOnline: string }): void {
    const dataInicio = this.montarDataIso(dia.dataIso, horario.inicio);
    const dataFim = this.montarDataIso(dia.dataIso, horario.fim);

    this.router.navigate(['/agendar-atendimento'], {
      queryParams: {
        // O select de assessor trabalha com ID criptografado (dropdown).
        // Para pré-seleção correta, sempre enviamos o valor selecionado no filtro.
        assessorId: this.assessorSelecionado || horario.idAssessor,
        data: dia.data,
        dataIso: dia.dataIso,
        dataInicio,
        dataFim,
        presencialOnline: horario.presencialOnline
      }
    });
  }

  private montarDataIso(dataIso: string, hora: string): string {
    return `${dataIso}T${hora.length === 5 ? `${hora}:00` : hora}`;
  }

  cadastroRapido(): void {
    this.router.navigate(['/cadastro-cliente']);
  }
}